/**
 * Ecomus 主题正计时组件 - 支持年份显示
 * 从指定的起始日期开始往上累加，显示：年、天、小时、分钟
 * 完全兼容 Ecomus 主题的样式和动画效果
 */

class CountUpWithYears extends HTMLElement {
  constructor() {
    super();
    
    this.selectors = {
      years: '[data-years]',
      days: '[data-days]',
      hours: '[data-hours]',
      minutes: '[data-minutes]',
      seconds: '[data-seconds]'
    };
    
    // 从配置中获取起始日期
    const config = JSON.parse(this.getAttribute('config') || '{}');
    const { month, date, year } = config;
    
    if (!year || !month || !date) {
      console.error('CountUp: year, month, and date are required in config');
      return;
    }
    
    // 解析日期字符串（格式：day, year hour:minute）
    const dateParts = date.split(',');
    const day = dateParts[0].trim();
    const yearTime = dateParts[1] ? dateParts[1].trim() : year;
    
    // 构建日期字符串
    const monthNames = {
      '1': 'January', '2': 'February', '3': 'March', '4': 'April',
      '5': 'May', '6': 'June', '7': 'July', '8': 'August',
      '9': 'September', '10': 'October', '11': 'November', '12': 'December'
    };
    
    const dateString = `${monthNames[month]} ${day}, ${yearTime}`;
    this.startDate = new Date(dateString);
    
    if (isNaN(this.startDate.getTime())) {
      console.error('CountUp: Invalid date format');
      return;
    }
    
    // 获取元素
    this.yearsEl = this.querySelector(this.selectors.years);
    this.daysEl = this.querySelector(this.selectors.days);
    this.hoursEl = this.querySelector(this.selectors.hours);
    this.minutesEl = this.querySelector(this.selectors.minutes);
    this.secondsEl = this.querySelector(this.selectors.seconds);
    
    this.playing = false;
    this.myReq = null;
  }
  
  connectedCallback() {
    this.init();
    
    // 支持页面可见性变化时的暂停/播放
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.play();
      } else {
        this.pause();
      }
    });
  }
  
  disconnectedCallback() {
    this.pause();
  }
  
  init() {
    if (!this.playing) {
      this.classList.add('hdt-cd--enabled');
      this.play();
    }
  }
  
  timerLoop() {
    const now = new Date();
    const elapsed = now - this.startDate;
    
    if (elapsed < 0) {
      // 如果起始日期在未来，显示 0
      this.updateDisplay(0, 0, 0, 0, 0);
      this.myReq = requestAnimationFrame(this.timerLoop.bind(this));
      return;
    }
    
    // 计算时间差（毫秒）
    const totalSeconds = Math.floor(elapsed / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);
    
    // 计算年份（考虑闰年，平均每年 365.25 天）
    const years = Math.floor(totalDays / 365.25);
    
    // 计算剩余天数（一年内的天数）
    const remainingDays = totalDays - Math.floor(years * 365.25);
    
    // 计算剩余小时、分钟、秒
    const remainingHours = totalHours % 24;
    const remainingMinutes = totalMinutes % 60;
    const remainingSeconds = totalSeconds % 60;
    
    this.updateDisplay(years, remainingDays, remainingHours, remainingMinutes, remainingSeconds);
    
    // 继续循环
    this.myReq = requestAnimationFrame(this.timerLoop.bind(this));
  }
  
  updateDisplay(years, days, hours, minutes, seconds) {
    // 更新年份
    if (this.yearsEl && this.yearsEl.updateValue) {
      this.yearsEl.updateValue(years, true);
    }
    
    // 更新天数
    if (this.daysEl && this.daysEl.updateValue) {
      this.daysEl.updateValue(days, true);
    }
    
    // 更新小时
    if (this.hoursEl && this.hoursEl.updateValue) {
      this.hoursEl.updateValue(hours, true);
    }
    
    // 更新分钟
    if (this.minutesEl && this.minutesEl.updateValue) {
      this.minutesEl.updateValue(minutes, true);
    }
    
    // 更新秒数
    if (this.secondsEl && this.secondsEl.updateValue) {
      this.secondsEl.updateValue(seconds, true);
    }
  }
  
  play() {
    if (!this.playing) {
      this.myReq = requestAnimationFrame(this.timerLoop.bind(this));
      this.playing = true;
    }
  }
  
  pause() {
    if (this.playing) {
      cancelAnimationFrame(this.myReq);
      this.playing = false;
    }
  }
}

// 注册自定义元素（避免重复注册）
if (!customElements.get('hdt-countup')) {
  customElements.define('hdt-countup', CountUpWithYears);
  console.log('Ecomus CountUp component with years support loaded successfully');
}
